import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { orderService, businessService } from '../services/api';
import { useSocketEvents } from '../hooks/useSocket';

interface OItem { id: number; product_name: string; product_price: number; quantity: number; notes?: string; status: string; course: number; course_name?: string; }
interface Order { id: number; table_id: number; table_name: string; room_name: string; status: string; items: OItem[]; bill_requested_at?: string | null; waiter_name?: string; created_at: string; }

const Page = styled.div`max-width: 1200px; margin: 0 auto; display: flex; flex-direction: column; gap: 20px;`;
const Header = styled.div`display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; gap: 8px;`;
const Title = styled.h1`font-size: 24px; font-weight: 700; color: #111827; margin: 0;`;
const Sub = styled.div`font-size: 13px; color: #6b7280;`;

const SectionTitle = styled.div`font-size: 13px; font-weight: 700; color: #9ca3af; text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: -6px;`;
const Grid = styled.div`display: grid; grid-template-columns: repeat(auto-fill, minmax(320px, 1fr)); gap: 16px;`;

const Card = styled.div<{ $requested?: boolean }>`
  background: white; border-radius: 14px;
  box-shadow: 0 1px 4px rgba(0,0,0,0.08);
  border-top: 4px solid ${p => p.$requested ? '#f59e0b' : '#e5e7eb'};
  overflow: hidden; display: flex; flex-direction: column;
`;
const CardHead = styled.div`padding: 14px 16px 10px; border-bottom: 1px solid #f3f4f6;`;
const TableName = styled.div`font-size: 18px; font-weight: 800; color: #1f2937;`;
const Meta = styled.div`font-size: 12px; color: #9ca3af; margin-top: 2px;`;
const ReqTag = styled.span`
  display: inline-block; margin-top: 6px; background: #fffbeb; color: #b45309;
  border: 1px solid #fde68a; border-radius: 20px; padding: 2px 10px; font-size: 12px; font-weight: 700;
`;

const Items = styled.div`padding: 8px 16px; display: flex; flex-direction: column; gap: 2px; flex: 1;`;
const CourseLbl = styled.div`font-size: 11px; font-weight: 700; color: #9ca3af; text-transform: uppercase; letter-spacing: 0.5px; margin-top: 8px;`;
const Row = styled.div`display: flex; align-items: baseline; gap: 8px; padding: 4px 0;`;
const Qty = styled.span`font-size: 14px; font-weight: 700; color: #6b7280; min-width: 26px;`;
const Name = styled.span`flex: 1; font-size: 14px; color: #1f2937;`;
const Note = styled.div`font-size: 11px; color: #9ca3af; font-style: italic;`;
const Price = styled.span`font-size: 14px; font-weight: 600; color: #374151;`;

const Adjust = styled.div`padding: 8px 16px; border-top: 1px solid #f3f4f6; display: flex; flex-direction: column; gap: 8px;`;
const AdjRow = styled.div`display: flex; align-items: center; gap: 8px;`;
const AdjLbl = styled.span`font-size: 13px; color: #6b7280; flex: 1;`;
const Stepper = styled.div`display: flex; align-items: center; gap: 6px;`;
const StepBtn = styled.button`
  width: 30px; height: 30px; border-radius: 50%; border: 1px solid #d1d5db;
  background: white; font-size: 17px; font-weight: 700; color: #374151; cursor: pointer;
  display: flex; align-items: center; justify-content: center;
  &:active { background: #f3f4f6; }
`;
const StepVal = styled.span`min-width: 22px; text-align: center; font-size: 15px; font-weight: 800; color: #1f2937;`;
const NumInput = styled.input`
  width: 74px; padding: 6px 8px; border: 1px solid #d1d5db; border-radius: 8px;
  font-size: 14px; text-align: right; box-sizing: border-box;
  &:focus { outline: none; border-color: #cc9d6d; }
`;
const RoundBtn = styled.button`
  padding: 6px 10px; border-radius: 8px; border: 1px solid #e5e7eb; background: white;
  color: #6b7280; font-size: 12px; font-weight: 600; cursor: pointer; white-space: nowrap;
  &:active { background: #f3f4f6; }
`;
const SubLine = styled.div`display: flex; justify-content: space-between; font-size: 12px; color: #9ca3af;`;

const TotalRow = styled.div`
  display: flex; justify-content: space-between; align-items: center;
  padding: 12px 16px; border-top: 1px solid #f3f4f6; background: #fafafa;
`;
const TotalLbl = styled.span`font-size: 16px; font-weight: 700; color: #111827;`;
const TotalVal = styled.span`font-size: 22px; font-weight: 800; color: #111827;`;

const Actions = styled.div`padding: 0 16px 16px; display: flex; gap: 8px;`;
const CloseBtn = styled.button`
  flex: 1; padding: 14px; border-radius: 10px; border: none;
  background: #16a34a; color: white; font-size: 15px; font-weight: 700; cursor: pointer;
  &:hover { background: #15803d; }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;
const GhostBtn = styled.button`
  padding: 14px 16px; border-radius: 10px; border: 1px solid #e5e7eb;
  background: white; color: #6b7280; font-size: 14px; font-weight: 600; cursor: pointer;
  &:hover { background: #f9fafb; }
`;

const EmptyState = styled.div`text-align: center; padding: 60px 20px; color: #9ca3af;
  h2 { font-size: 18px; margin: 0 0 8px; } p { font-size: 14px; margin: 0; }`;

function elapsed(iso?: string | null) {
  if (!iso) return '';
  const diff = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
  if (diff < 1) return 'ora';
  if (diff === 1) return '1 min fa';
  return `${diff} min fa`;
}

const Cassa: React.FC = () => {
  const { on } = useSocketEvents();
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [closing, setClosing] = useState<number | null>(null);
  const [, setTick] = useState(0);
  // Modifiche conto per ordine: coperti, prezzo coperto, arrotondamento/sconto
  const [edits, setEdits] = useState<Record<number, { covers: number; coverPrice: number; adjustment: number }>>({});
  // Impostazioni ristorante (coperto di default)
  const [biz, setBiz] = useState<{ name: string; coverCharge: number; defaultCovers: number }>({ name: '', coverCharge: 0, defaultCovers: 0 });

  const getEdit = (id: number) => edits[id] || { covers: biz.defaultCovers, coverPrice: biz.coverCharge, adjustment: 0 };
  const setEdit = (id: number, patch: Partial<{ covers: number; coverPrice: number; adjustment: number }>) =>
    setEdits(prev => ({ ...prev, [id]: { ...getEdit(id), ...patch } }));

  const reload = useCallback(async () => {
    const res = await orderService.getActive();
    setOrders(res.data?.orders || []);
  }, []);

  useEffect(() => { reload().finally(() => setLoading(false)); }, [reload]);

  useEffect(() => {
    businessService.get().then(d => setBiz({
      name: d?.name || '',
      coverCharge: parseFloat(d?.cover_charge) || 0,
      defaultCovers: parseInt(d?.default_covers) || 0,
    })).catch(() => {});
  }, []);

  useEffect(() => {
    const t = setInterval(() => setTick(n => n + 1), 60000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    const cleanups = [
      on('order:new', reload),
      on('order:updated', reload),
      on('order:bill_requested', reload),
      on('order:item_added', reload),
      on('order:item_updated', reload),
      on('order:item_removed', reload),
    ];
    return () => cleanups.forEach(fn => fn());
  }, [on, reload]);

  const orderTotal = (o: Order) => o.items.reduce((s, i) => s + i.product_price * i.quantity, 0);
  const coverTotal = (o: Order) => { const e = getEdit(o.id); return e.covers * e.coverPrice; };
  const finalTotal = (o: Order) => orderTotal(o) + coverTotal(o) + getEdit(o.id).adjustment;

  const handleClose = async (o: Order) => {
    const e = getEdit(o.id);
    const ft = finalTotal(o);
    if (!window.confirm(`Chiudere il tavolo ${o.table_name} per € ${ft.toFixed(2)}? Il tavolo tornerà libero.`)) return;
    setClosing(o.id);
    try {
      await orderService.closeOrder(o.id, {
        covers: e.covers, cover_price: e.coverPrice, adjustment: e.adjustment, final_total: ft,
      });
      setEdits(prev => { const n = { ...prev }; delete n[o.id]; return n; });
      await reload();
    } finally {
      setClosing(null);
    }
  };

  const handleCancelReq = async (o: Order) => {
    await orderService.cancelBill(o.id);
    await reload();
  };

  const printPreconto = (o: Order) => {
    const e = getEdit(o.id);
    const esc = (s: string) => String(s).replace(/[<>&]/g, c => ({ '<': '&lt;', '>': '&gt;', '&': '&amp;' }[c] || c));
    const rows = o.items.map(i => `
      <tr>
        <td>${i.quantity}×</td>
        <td>${esc(i.product_name)}${i.notes ? `<div class="note">${esc(i.notes)}</div>` : ''}</td>
        <td class="r">€ ${(i.product_price * i.quantity).toFixed(2)}</td>
      </tr>`).join('');
    const coverRow = e.covers > 0
      ? `<tr><td>${e.covers}×</td><td>Coperto</td><td class="r">€ ${(e.covers * e.coverPrice).toFixed(2)}</td></tr>` : '';
    const adjRow = e.adjustment !== 0
      ? `<tr><td></td><td>Arrotondamento/sconto</td><td class="r">€ ${e.adjustment.toFixed(2)}</td></tr>` : '';
    const now = new Date();
    const html = `<!doctype html><html><head><meta charset="utf-8"><title>Preconto</title>
      <style>
        * { font-family: 'Courier New', monospace; }
        body { width: 280px; margin: 0 auto; padding: 12px; color: #000; }
        h1 { font-size: 16px; text-align: center; margin: 0 0 2px; }
        .sub { text-align: center; font-size: 12px; margin-bottom: 10px; }
        table { width: 100%; border-collapse: collapse; font-size: 12px; }
        td { padding: 2px 0; vertical-align: top; }
        td:first-child { width: 32px; }
        .r { text-align: right; white-space: nowrap; }
        .note { font-size: 10px; font-style: italic; color: #444; }
        .tot { border-top: 1px dashed #000; margin-top: 8px; padding-top: 6px;
               display: flex; justify-content: space-between; font-size: 16px; font-weight: bold; }
        .foot { text-align: center; font-size: 11px; margin-top: 14px; }
      </style></head><body>
      <h1>${esc(biz.name || 'Preconto')}</h1>
      <div class="sub">${esc(o.room_name)} — Tavolo ${esc(o.table_name)}<br>${now.toLocaleDateString('it-IT')} ${now.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })}</div>
      <table>${rows}${coverRow}${adjRow}</table>
      <div class="tot"><span>TOTALE</span><span>€ ${finalTotal(o).toFixed(2)}</span></div>
      <div class="foot">Preconto — non è documento fiscale</div>
      <script>window.onload = function(){ window.print(); }<\/script>
      </body></html>`;
    const w = window.open('', '_blank', 'width=340,height=600');
    if (w) { w.document.write(html); w.document.close(); }
  };

  const requested = orders
    .filter(o => o.bill_requested_at && o.items.length > 0)
    .sort((a, b) => new Date(a.bill_requested_at!).getTime() - new Date(b.bill_requested_at!).getTime());
  const others = orders.filter(o => !o.bill_requested_at && o.items.length > 0);

  const renderCard = (o: Order, requestedCard: boolean) => {
    const courses = Array.from(new Set(o.items.map(i => i.course))).sort((a, b) => a - b);
    return (
      <Card key={o.id} $requested={requestedCard}>
        <CardHead>
          <TableName>{o.room_name} — Tavolo {o.table_name}</TableName>
          <Meta>{o.waiter_name ? `Cameriere: ${o.waiter_name}` : 'Ordine cliente'}</Meta>
          {requestedCard && <ReqTag>💰 Conto richiesto · {elapsed(o.bill_requested_at)}</ReqTag>}
        </CardHead>
        <Items>
          {courses.map(c => (
            <div key={c}>
              <CourseLbl>{o.items.find(i => i.course === c)?.course_name || `Portata ${c}`}</CourseLbl>
              {o.items.filter(i => i.course === c).map(i => (
                <Row key={i.id}>
                  <Qty>{i.quantity}×</Qty>
                  <div style={{ flex: 1 }}>
                    <Name>{i.product_name}</Name>
                    {i.notes && <Note>📝 {i.notes}</Note>}
                  </div>
                  <Price>€ {(i.product_price * i.quantity).toFixed(2)}</Price>
                </Row>
              ))}
            </div>
          ))}
        </Items>
        {(() => {
          const e = getEdit(o.id);
          const base = orderTotal(o);
          return (
            <Adjust>
              <AdjRow>
                <AdjLbl>Coperti</AdjLbl>
                <Stepper>
                  <StepBtn onClick={() => setEdit(o.id, { covers: Math.max(0, e.covers - 1) })}>−</StepBtn>
                  <StepVal>{e.covers}</StepVal>
                  <StepBtn onClick={() => setEdit(o.id, { covers: e.covers + 1 })}>+</StepBtn>
                </Stepper>
                <span style={{ fontSize: 13, color: '#9ca3af' }}>×</span>
                <NumInput
                  type="number" step="0.10" min="0" value={e.coverPrice || ''}
                  placeholder="0,00"
                  onChange={ev => setEdit(o.id, { coverPrice: parseFloat(ev.target.value) || 0 })}
                />
              </AdjRow>
              <AdjRow>
                <AdjLbl>Arrotondamento / sconto</AdjLbl>
                <RoundBtn onClick={() => {
                  const preRound = base + e.covers * e.coverPrice + e.adjustment;
                  const target = Math.floor(preRound);
                  setEdit(o.id, { adjustment: e.adjustment + (target - preRound) });
                }}>Arrotonda ↓</RoundBtn>
                <NumInput
                  type="number" step="0.10" value={e.adjustment || ''}
                  placeholder="0,00"
                  onChange={ev => setEdit(o.id, { adjustment: parseFloat(ev.target.value) || 0 })}
                />
              </AdjRow>
              <SubLine>
                <span>Piatti € {base.toFixed(2)}{e.covers > 0 ? ` · Coperti € ${(e.covers * e.coverPrice).toFixed(2)}` : ''}{e.adjustment !== 0 ? ` · Arr./sconto € ${e.adjustment.toFixed(2)}` : ''}</span>
              </SubLine>
            </Adjust>
          );
        })()}
        <TotalRow>
          <TotalLbl>Totale da incassare</TotalLbl>
          <TotalVal>€ {finalTotal(o).toFixed(2)}</TotalVal>
        </TotalRow>
        <Actions>
          <CloseBtn onClick={() => handleClose(o)} disabled={closing === o.id}>
            {closing === o.id ? 'Chiusura…' : '✓ Chiudi tavolo (incassato)'}
          </CloseBtn>
          <GhostBtn onClick={() => printPreconto(o)} title="Stampa preconto">🧾 Preconto</GhostBtn>
          {requestedCard && (
            <GhostBtn onClick={() => handleCancelReq(o)}>Annulla</GhostBtn>
          )}
        </Actions>
      </Card>
    );
  };

  if (loading) return <Page><p style={{ color: '#6b7280' }}>Caricamento...</p></Page>;

  return (
    <Page>
      <Header>
        <Title>Cassa</Title>
        <Sub>{requested.length} {requested.length === 1 ? 'conto richiesto' : 'conti richiesti'} · {others.length} tavoli aperti</Sub>
      </Header>

      {requested.length === 0 && others.length === 0 ? (
        <EmptyState>
          <h2>Nessun tavolo aperto</h2>
          <p>Quando un cameriere richiede il conto comparirà qui.</p>
        </EmptyState>
      ) : (
        <>
          {requested.length > 0 && (
            <>
              <SectionTitle>Conti richiesti</SectionTitle>
              <Grid>{requested.map(o => renderCard(o, true))}</Grid>
            </>
          )}
          {others.length > 0 && (
            <>
              <SectionTitle>Altri tavoli aperti</SectionTitle>
              <Grid>{others.map(o => renderCard(o, false))}</Grid>
            </>
          )}
        </>
      )}
    </Page>
  );
};

export default Cassa;
